// submitTriggerPrivateStream.js
'use strict';

import { v4 as uuidv4 } from 'uuid';

import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getMeData from '../selector/getMeData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import { getLiveStreamExclusivePrivatePrice } from '../selector/getLiveStreamPrice.js';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import {
  PRESET_SD,
  LivestreamTriggerType,
  livestreamPaymentVia,
} from '../resource/liveStreamConstants.js';
import { EventTypes } from '../resource/trackingConstants.js';

import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import {
  LIVESTREAM_PAY_DURATION_SECONDS,
  TRIGGER_PRIVATE_WAITING_TIME,
  TRIGGER_EXCLUSIVE_HOLDINGTIME,
  TRIGGER_EXCLUSIVE_WAITING_TIME,
} from '../RemoteConfigKeys.js';

import submitLivestreamPaymentTracking from '../action/submitLivestreamPaymentTracking.js';

const LivestreamTriggerInfo = {
  [LivestreamTriggerType.TRIGGER_EXCLUSIVE]: {
    endpoint: 'trigger_exclusive',
    goalDurationConfigKey: TRIGGER_EXCLUSIVE_WAITING_TIME,
    prepayDurationConfigKey: TRIGGER_EXCLUSIVE_HOLDINGTIME,
  },
  [LivestreamTriggerType.TRIGGER_PRIVATE]: {
    endpoint: 'trigger_private',
    goalDurationConfigKey: TRIGGER_PRIVATE_WAITING_TIME,
    prepayDurationConfigKey: LIVESTREAM_PAY_DURATION_SECONDS,
  },
};

/**
 * Submit trigger private stream
 * @kind action
 * @param {string} {sessionId} - livestream session id.
 * @param {LivestreamTriggerType} {triggerType} - trigger type.
 * @return {Promise} Action promise.
 *
 */
const submitTriggerPrivateStream =
  ({ sessionId, triggerType, streamId } = {}) =>
  async (dispatch, getState) => {
    const networkingSelectPath = ['submit', triggerType, sessionId];
    const token = getMeData(getState(), 'token');
    const triggerInfo = LivestreamTriggerInfo[triggerType];
    if (!token || !sessionId || !triggerInfo || !streamId) {
      return dispatch({ type: '' });
    }

    const isExclusive = triggerType === LivestreamTriggerType.TRIGGER_EXCLUSIVE;
    const requestId = uuidv4();
    const { endpoint, goalDurationConfigKey, prepayDurationConfigKey } =
      triggerInfo;

    const paymentTrackingPayload = {
      streamId,
      sessionId,
      requestId,
      via: livestreamPaymentVia.CLICK,
      price: isExclusive
        ? getLiveStreamExclusivePrivatePrice(getState(), streamId)
        : getLiveStreamData(getState(), [streamId, 'pricing'], PRESET_SD),
      duration: getRemoteConfigData(getState(), prepayDurationConfigKey),
      privateTriggerType: triggerType,
    };

    dispatch(
      submitLivestreamPaymentTracking({
        trackingType: EventTypes.LIVESTREAM_PAYMENT,
        ...paymentTrackingPayload,
      })
    );

    const nowUnix = getCurrentUnixTimestamp({
      offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
    });
    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Request-ID': requestId,
      },
      body: JSON.stringify({
        exp: nowUnix + getRemoteConfigData(getState(), goalDurationConfigKey),
        duration: getRemoteConfigData(getState(), prepayDurationConfigKey),
      }),
    };
    const fetchUrl = getResourceUrl({
      endpoint: `/sessions/${sessionId}/${endpoint}`,
    });
    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);

      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      dispatch(
        submitLivestreamPaymentTracking({
          trackingType: EventTypes.LIVESTREAM_PAYMENT_ERROR,
          ...paymentTrackingPayload,
          error,
        })
      );
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default submitTriggerPrivateStream;
